































































































import { Vue, Component, Watch } from 'vue-property-decorator';
import { IMAGES_MIME, MAX_FILE_SIZE } from '@common/app.config';
import { MESSAGE_TYPE } from '@common/enums/message';
import Toast from '@common/helpers/toast';
import LocalStorageUtils from '@common/LocalStorageUtils';
import ApiHelper from 'api-helper';
import { State, Action } from 'vuex-class';
import { UserTreatment } from '@/models/user-treatment';
import { MessageEvent } from '@/events/message';

const MAX_LENGTH_MESSAGE = 2000;

@Component
export default class MessageInputModal extends Vue {
  @State((state) => state.auth.currentUser) currentUser!: any;
  @State((state) => state.treatment_detail.data)
  activeTreatment!: UserTreatment;
  @State((state) => state.draft_message.message)
  draftMessage!: string;
  @Action('draft_message/updateDraft') updateDraft!: (msg: string) => void;
  @Action('draft_message/resetDraft') resetDraft!: () => void;

  currentChannel!: any;
  messageChange!: any;
  message = '';
  isShiftEnter = LocalStorageUtils.getItem('isShiftEnter') || true;
  isSending = false;
  accept = IMAGES_MIME.join();
  isSendError: boolean = false;

  get fileInput() {
    return this.$refs.fileInput as HTMLInputElement;
  }

  created() {
    MessageEvent.$on('edit-message', (payload) => {
      this.messageChange = payload?.message ? { ...payload.message } : null;

      if (this.messageChange) {
        this.message = this.messageChange.message_content;
        this.$nextTick(() => {
          this.autoResize();
          document.getElementById('inputMessage')?.focus();
        });
      }
    });

    MessageEvent.$on('reset-edit', () => {
      this.resetMessage();
    });
  }

  beforeDestroy() {
    MessageEvent.$off('edit-message');
    MessageEvent.$off('reset-edit');
  }

  open() {
    this.$modal.show('message-input-modal');
  }

  close() {
    this.$modal.hide('message-input-modal');
  }

  handleBeforeClose() {
    if (this.messageChange) {
      this.resetDraft();
    }
    this.messageChange = null;
  }

  autoResize() {
    const textarea = this.$refs.messageInput as HTMLTextAreaElement;
    if (!textarea) {
      return;
    }

    const scrollHeight = textarea.scrollHeight;
    if (scrollHeight > 200) {
      textarea.classList.add('scrollable');
    } else {
      textarea.classList.remove('scrollable');
    }

    textarea.style.height = 'auto';
    const lineHeight = 20;
    const minHeight = lineHeight * 3;

    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight));
    textarea.style.height = `${newHeight}px`;
  }

  @Watch('message')
  onMessageChange(newVal: string) {
    this.updateDraft(newVal);
  }

  resetMessage() {
    if (!this.isSendError) {
      this.message = '';
      this.messageChange = null;
    }

    this.isSendError = false;
    this.isSending = false;
    (this.$refs.fileInput as HTMLInputElement).value = '';
    this.$nextTick(() => {
      this.autoResize();
    });
    return;
  }

  sendMessageWhenKeyPress(event: KeyboardEvent) {
    if (event.shiftKey) {
      if (!this.isShiftEnter) {
        return;
      }
      this.sendTextMessage(event);
      return;
    }
    if (this.isShiftEnter) {
      return;
    }
    this.sendTextMessage(event);
  }

  mounted() {
    this.autoResize();
  }

  sendTextMessage(event: Event) {
    event.preventDefault();
    if (!this.message) {
      return;
    }

    const params = {
      channelId: this.currentChannel.id,
      message: this.message,
      image: null,
      type: MESSAGE_TYPE.TEXT,
    };
    this.sendMessageToUser(params);
  }

  onPaste(event: ClipboardEvent) {
    if (event.clipboardData) {
      const content = event.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, content);
    }
    if (this.message.length > MAX_LENGTH_MESSAGE) {
      this.message = this.message.substr(0, MAX_LENGTH_MESSAGE);
    }
  }

  checkMessageLength(event: KeyboardEvent) {
    if (this.message.length >= MAX_LENGTH_MESSAGE && !(event.which < 0x20)) {
      event.preventDefault();
    }
  }

  async sendAttachment(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files?.length || this.isSending) {
      return;
    }

    const file = files[0];
    if (file.size > MAX_FILE_SIZE) {
      Toast.error('サイズが5MBを超える画像はアップロードできません');
      return;
    }
    if (!IMAGES_MIME.includes(file.type)) {
      Toast.error('jpg又はpng形式でアップロードしてください');
      return;
    }

    const params = {
      channelId: this.currentChannel.id,
      message: null,
      image: file,
      type: MESSAGE_TYPE.IMAGE,
    };
    this.sendMessageToUser(params);
  }

  async sendMessageToUser({ channelId, message, image, type }) {
    try {
      this.isSending = true;

      if (!this.messageChange) {
        await ApiHelper.getApi('MessageApi').sendMessage(channelId, {
          message,
          image,
          type,
          user_treatment_id: this.activeTreatment.id,
        });
      } else {
        await ApiHelper.getApi('MessageApi').updateMessage(
          this.messageChange.id,
          {
            message,
            image,
            type,
            user_treatment_id: this.activeTreatment.id,
            _method: 'PUT',
          },
        );
      }
    } catch (error) {
      const msg =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'エラーが発生しました。もう一度お試しください。';

      Toast.error(msg);
      this.isSendError = true;
    } finally {
      this.isSending = false;
      this.resetMessage();
    }
  }

  updateIsShiftEnter() {
    LocalStorageUtils.saveItem('isShiftEnter', this.isShiftEnter);
  }

  onOpened() {
    this.$nextTick(() => {
      this.autoResize();
      this.enableDragging();
    });
  }

  enableDragging() {
    const modalRef = this.$refs.messageInputModal as any;
    const modal = modalRef.$el.querySelector('.v--modal-box') as HTMLElement;
    const header = this.$refs.dragHeader as HTMLElement;
    if (!modal || !header) {
      return;
    }

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let initialLeft = 0;
    let initialTop = 0;

    header.style.cursor = 'move';

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;

      const rect = modal.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;

      modal.style.position = 'fixed';
      modal.style.margin = '0';

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) {
        return;
      }

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      const newLeft = Math.min(
        Math.max(0, initialLeft + dx),
        window.innerWidth - modal.offsetWidth,
      );
      const newTop = Math.min(
        Math.max(0, initialTop + dy),
        window.innerHeight - modal.offsetHeight,
      );

      modal.style.left = `${newLeft}px`;
      modal.style.top = `${newTop}px`;
    };

    const onMouseUp = () => {
      isDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    header.addEventListener('mousedown', onMouseDown);
  }

  handleBeforeOpen(event) {
    this.currentChannel = event.params.currentChannel;

    if (this.messageChange) {
      this.message = this.messageChange.message_content;
    } else if (this.draftMessage) {
      this.message = this.draftMessage;
    } else {
      this.message = '';
    }

    this.$nextTick(() => this.autoResize());
  }
}
